import { type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { formatIsoDate, parseIsoDate } from './dateCore';
import type { DateLocaleData } from './dateLocale';

export interface CalendarInfinityMarkersProps {
  locale: DateLocaleData;
  mode: 'single' | 'range';
  from: string | null;
  to: string | null;
  hoverIso: string | null;
  hoverDir: 'fwd' | 'bwd';
  infHover: boolean;
  openStart: boolean;
  openEnd: boolean;
  viewYear: number;
  viewMonth: number;
  cells: Date[];
  cellW: number;
  cellH: number;
  onInfEnter: (dir: 'fwd' | 'bwd') => void;
  onInfLeave: () => void;
  onClickInfinity: (dir: 'fwd' | 'bwd') => void;
}

/** The floating ∞ hover button + the static ∞ badge for a filled open interval. Absolute overlay. */
export function CalendarInfinityMarkers({
  locale,
  mode,
  from,
  to,
  hoverIso,
  hoverDir,
  infHover,
  openStart,
  openEnd,
  viewYear,
  viewMonth,
  cells,
  cellW,
  cellH,
  onInfEnter,
  onInfLeave,
  onClickInfinity,
}: CalendarInfinityMarkersProps) {
  if (mode !== 'range') return null;
  const fromReal = !!from && from !== '-INF' && from !== 'INF';
  const toReal = !!to && to !== 'INF' && to !== '-INF';
  const nodes: ReactNode[] = [];

  if (fromReal && !to && (hoverIso || infHover)) {
    const dir = hoverDir || 'fwd';
    const allow = (dir === 'fwd' && openEnd) || (dir === 'bwd' && openStart);
    if (allow) {
      nodes.push(
        <button
          key={`inf-${dir}`}
          type="button"
          title={locale.rangeFrom}
          onMouseEnter={() => onInfEnter(dir)}
          onMouseLeave={onInfLeave}
          onClick={() => onClickInfinity(dir)}
          className={cn(
            'border-phx-border-3 bg-phx-surface text-phx-text-2 shadow-phx-md',
            'hover:bg-phx-primary-tint hover:border-phx-primary hover:text-phx-primary',
            'absolute z-[75] inline-flex h-7 w-[30px] items-center justify-center rounded border border-dashed text-base font-bold transition-colors'
          )}
          style={
            dir === 'fwd'
              ? { right: 0, bottom: 0, transform: 'translate(66%,68%)' }
              : { left: 0, top: 0, transform: 'translate(-66%,-68%)' }
          }
        >
          ∞
        </button>
      );
    }
  }

  const staticMarker = (key: string, col: number, row: number, shiftX: number) => (
    <div
      key={key}
      title="∞"
      className="bg-phx-primary text-phx-primary-fg pointer-events-none absolute z-[6] flex items-center justify-center rounded text-[15px] font-bold"
      style={{
        left: col * cellW,
        top: row * cellH,
        width: cellW,
        height: cellH,
        transform: shiftX ? `translate(${shiftX}%,0)` : 'none',
      }}
    >
      ∞
    </div>
  );

  if (to === 'INF' && fromReal) {
    const fd = parseIsoDate(from as string);
    if (
      fd &&
      (viewYear > fd.getFullYear() || (viewYear === fd.getFullYear() && viewMonth >= fd.getMonth()))
    ) {
      nodes.push(staticMarker('sel-inf', 6, 5, from === formatIsoDate(cells[41]!) ? -100 : 0));
    }
  }
  if (from === '-INF' && toReal) {
    const td = parseIsoDate(to as string);
    if (
      td &&
      (viewYear < td.getFullYear() || (viewYear === td.getFullYear() && viewMonth <= td.getMonth()))
    ) {
      nodes.push(staticMarker('sel-ninf', 0, 0, to === formatIsoDate(cells[0]!) ? 100 : 0));
    }
  }
  return <>{nodes}</>;
}
